import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PortalHeader from './PortalHeader';
import './MonochromeLogo.css';

const MAX_PREVIEW_SIZE = 1024;
const OUTPUT_SIZE = 512;
const EDGE_FEATHER = 18;
const SUPERSAMPLE_SCALE = 2;

function MonochromeLogo() {
    const fileInputRef = useRef(null);
    const sourceImageRef = useRef(null);
    const [fileName, setFileName] = useState('');
    const [sourceUrl, setSourceUrl] = useState('');
    const [previewUrl, setPreviewUrl] = useState('');
    const [threshold, setThreshold] = useState(245);
    const [backgroundMode, setBackgroundMode] = useState('transparent');
    const [error, setError] = useState('');

    useEffect(() => () => {
        if (sourceUrl) URL.revokeObjectURL(sourceUrl);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
    }, [sourceUrl, previewUrl]);

    const getForegroundAlpha = (mode, luminance, alpha, cutoff) => {
        if (mode === 'transparent') return alpha;

        const distanceFromBackground = mode === 'dark'
            ? luminance - cutoff
            : cutoff - luminance;
        const edgeCoverage = Math.max(0, Math.min(1, distanceFromBackground / EDGE_FEATHER));

        return Math.round(alpha * edgeCoverage);
    };

    const analyzeBackground = (image) => {
        const sampleCanvas = document.createElement('canvas');
        sampleCanvas.width = 64;
        sampleCanvas.height = 64;
        const sampleContext = sampleCanvas.getContext('2d', { willReadFrequently: true });
        sampleContext.drawImage(image, 0, 0, 64, 64);
        const sample = sampleContext.getImageData(0, 0, 64, 64).data;
        let hasTransparency = false;
        let cornerLuminance = 0;
        let cornerCount = 0;

        for (let y = 0; y < 64; y += 1) {
            for (let x = 0; x < 64; x += 1) {
                const index = ((y * 64) + x) * 4;
                if (sample[index + 3] < 250) hasTransparency = true;

                const isCorner = (x < 6 || x >= 58) && (y < 6 || y >= 58);
                if (isCorner) {
                    cornerLuminance += (0.299 * sample[index])
                        + (0.587 * sample[index + 1])
                        + (0.114 * sample[index + 2]);
                    cornerCount += 1;
                }
            }
        }

        if (hasTransparency) return { mode: 'transparent', cutoff: threshold };

        const averageCorner = cornerLuminance / Math.max(1, cornerCount);
        const mode = averageCorner >= 128 ? 'light' : 'dark';
        const offset = mode === 'light' ? -8 : 8;
        return {
            mode,
            cutoff: Math.max(5, Math.min(250, Math.round(averageCorner + offset))),
        };
    };

    const createMonochrome = (
        image,
        mode = backgroundMode,
        cutoff = threshold,
    ) => {
        const sourceWidth = image.naturalWidth || image.width;
        const sourceHeight = image.naturalHeight || image.height;
        const scale = Math.min(1, MAX_PREVIEW_SIZE / Math.max(sourceWidth, sourceHeight));
        const width = Math.max(1, Math.round(sourceWidth * scale));
        const height = Math.max(1, Math.round(sourceHeight * scale));
        const sourceCanvas = document.createElement('canvas');
        sourceCanvas.width = width;
        sourceCanvas.height = height;
        const sourceContext = sourceCanvas.getContext('2d', { willReadFrequently: true });
        sourceContext.clearRect(0, 0, width, height);
        sourceContext.drawImage(image, 0, 0, width, height);

        const pixels = sourceContext.getImageData(0, 0, width, height);
        const maskCanvas = document.createElement('canvas');
        maskCanvas.width = width;
        maskCanvas.height = height;
        const maskContext = maskCanvas.getContext('2d');
        const outputPixels = maskContext.createImageData(width, height);
        const foregroundMask = new Uint8Array(width * height);

        for (let pixelIndex = 0; pixelIndex < foregroundMask.length; pixelIndex += 1) {
            const dataIndex = pixelIndex * 4;
            const red = pixels.data[dataIndex];
            const green = pixels.data[dataIndex + 1];
            const blue = pixels.data[dataIndex + 2];
            const alpha = pixels.data[dataIndex + 3];
            const luminance = (0.299 * red) + (0.587 * green) + (0.114 * blue);
            foregroundMask[pixelIndex] = getForegroundAlpha(mode, luminance, alpha, cutoff);
        }

        for (let pixelIndex = 0; pixelIndex < foregroundMask.length; pixelIndex += 1) {
            const dataIndex = pixelIndex * 4;
            outputPixels.data[dataIndex] = 255;
            outputPixels.data[dataIndex + 1] = 255;
            outputPixels.data[dataIndex + 2] = 255;
            outputPixels.data[dataIndex + 3] = foregroundMask[pixelIndex];
        }

        maskContext.putImageData(outputPixels, 0, 0);

        const renderSize = OUTPUT_SIZE * SUPERSAMPLE_SCALE;
        const renderCanvas = document.createElement('canvas');
        renderCanvas.width = renderSize;
        renderCanvas.height = renderSize;
        const renderContext = renderCanvas.getContext('2d');
        const outputScale = Math.min((renderSize * 0.82) / width, (renderSize * 0.82) / height);
        const outputWidth = Math.max(1, Math.round(width * outputScale));
        const outputHeight = Math.max(1, Math.round(height * outputScale));
        renderContext.imageSmoothingEnabled = true;
        renderContext.imageSmoothingQuality = 'high';
        renderContext.drawImage(
            maskCanvas,
            Math.round((renderSize - outputWidth) / 2),
            Math.round((renderSize - outputHeight) / 2),
            outputWidth,
            outputHeight,
        );

        const output = document.createElement('canvas');
        output.width = OUTPUT_SIZE;
        output.height = OUTPUT_SIZE;
        const outputContext = output.getContext('2d');
        outputContext.imageSmoothingEnabled = true;
        outputContext.imageSmoothingQuality = 'high';
        outputContext.filter = 'blur(0.35px)';
        outputContext.drawImage(renderCanvas, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
        return new Promise((resolve, reject) => {
            output.toBlob((blob) => {
                if (!blob) {
                    reject(new Error('The PNG could not be created.'));
                    return;
                }
                resolve(URL.createObjectURL(blob));
            }, 'image/png');
        });
    };

    const processFile = (file) => {
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setError('Please choose a PNG, JPG, WEBP, GIF, or SVG image.');
            return;
        }

        const nextSourceUrl = URL.createObjectURL(file);
        const image = new Image();
        image.onload = async () => {
            try {
                const suggestedBackground = analyzeBackground(image);
                const convertedUrl = await createMonochrome(
                    image,
                    suggestedBackground.mode,
                    suggestedBackground.cutoff,
                );
                setError('');
                sourceImageRef.current = image;
                setSourceUrl((previousUrl) => {
                    if (previousUrl) URL.revokeObjectURL(previousUrl);
                    return nextSourceUrl;
                });
                setFileName(file.name);
                setBackgroundMode(suggestedBackground.mode);
                setThreshold(suggestedBackground.cutoff);
                setPreviewUrl(convertedUrl);
            } catch (conversionError) {
                URL.revokeObjectURL(nextSourceUrl);
                setError(conversionError.message);
            }
        };
        image.onerror = () => {
            URL.revokeObjectURL(nextSourceUrl);
            setError('Unable to read this image. Export it as a PNG or SVG file and try again.');
        };
        image.src = nextSourceUrl;
    };

    const handleDrop = (event) => {
        event.preventDefault();
        processFile(event.dataTransfer.files[0]);
    };

    const updatePreview = async (
        nextMode = backgroundMode,
        nextThreshold = threshold,
    ) => {
        if (sourceImageRef.current) {
            try {
                const convertedUrl = await createMonochrome(
                    sourceImageRef.current,
                    nextMode,
                    nextThreshold,
                );
                setPreviewUrl(convertedUrl);
                setError('');
            } catch (conversionError) {
                setError(conversionError.message);
            }
        }
    };

    const handleModeChange = (event) => {
        const nextMode = event.target.value;
        setBackgroundMode(nextMode);
        updatePreview(nextMode, threshold);
    };

    const handleThresholdChange = (event) => {
        const nextThreshold = Number(event.target.value);
        setThreshold(nextThreshold);
        updatePreview(backgroundMode, nextThreshold);
    };

    const handleDownload = () => {
        if (!previewUrl) return;
        const download = document.createElement('a');
        const baseName = fileName.replace(/\.[^/.]+$/, '') || 'notification-icon';
        download.href = previewUrl;
        download.download = `${baseName}-notification-monochrome.png`;
        download.click();
    };

    return (
        <div className="monochrome-page">
            <PortalHeader>
                <Link to="/dashboard" className="monochrome-back-link">Return to dashboard</Link>
            </PortalHeader>

            <main className="monochrome-main">
                <section className="monochrome-intro">
                    <p className="monochrome-eyebrow">Android icon utility</p>
                    <h1>Create an Android notification icon</h1>
                    <p>Convert a logo into a white silhouette on a transparent background for an Android notification icon.</p>
                </section>

                <section className="monochrome-workspace">
                    <div className="monochrome-controls">
                        <div
                            className="monochrome-dropzone"
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') fileInputRef.current?.click();
                            }}
                            role="button"
                            tabIndex="0"
                            aria-label="Choose a logo image"
                        >
                            <span className="dropzone-icon">＋</span>
                            <strong>{fileName || 'Upload a logo'}</strong>
                            <span>{fileName ? 'Choose another file' : 'Drag a file here or browse files'}</span>
                            <small>PNG, JPG, WEBP, GIF, or SVG</small>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                                onChange={(event) => processFile(event.target.files[0])}
                            />
                        </div>

                        <details className="monochrome-settings">
                            <summary>Background removal</summary>
                            <div className="monochrome-settings-body">
                                <label htmlFor="background-mode">Source background</label>
                                <select id="background-mode" value={backgroundMode} onChange={handleModeChange}>
                                    <option value="transparent">Transparent background</option>
                                    <option value="light">Remove light background</option>
                                    <option value="dark">Remove dark background</option>
                                </select>
                                {backgroundMode !== 'transparent' && (
                                    <>
                                        <div className="threshold-label">
                                            <label htmlFor="threshold">Background cutoff</label>
                                            <span>{threshold}</span>
                                        </div>
                                        <input
                                            id="threshold"
                                            type="range"
                                            min="0"
                                            max="255"
                                            value={threshold}
                                            onChange={handleThresholdChange}
                                        />
                                        <p>
                                            {backgroundMode === 'light'
                                                ? 'Higher values remove more light pixels.'
                                                : 'Lower values remove more dark pixels.'}
                                            {' '}The initial threshold is estimated from the image corners.
                                        </p>
                                    </>
                                )}
                            </div>
                        </details>

                        <div className="monochrome-proof">
                            <span className="proof-check">✓</span>
                            <div>
                                <strong>Output format</strong>
                                <p>White silhouette with transparency for import into Image Asset Studio.</p>
                            </div>
                        </div>
                    </div>

                    <div className="monochrome-preview-panel">
                        <div className="preview-heading">
                            <div>
                                <p className="preview-label">Preview</p>
                                <h2>{previewUrl ? 'Ready to download' : 'Icon preview'}</h2>
                            </div>
                            <span className="preview-size">PNG · 512 × 512</span>
                        </div>
                        <div className="monochrome-preview checkerboard">
                            {previewUrl ? (
                                <img src={previewUrl} alt="Monochrome notification icon preview" />
                            ) : (
                                <div className="preview-placeholder"><span>◎</span><p>Upload a logo to generate a preview</p></div>
                            )}
                        </div>
                        <button type="button" className="monochrome-download" onClick={handleDownload} disabled={!previewUrl}>
                            Download monochrome PNG <span aria-hidden="true">↓</span>
                        </button>
                        {error && <p className="monochrome-error">{error}</p>}
                    </div>
                </section>

                <p className="monochrome-footnote">Tip: in Android Studio, right-click <code>res</code> → <code>New</code> → <code>Image Asset</code>, then select the downloaded PNG as your foreground layer.</p>
            </main>
        </div>
    );
}

export default MonochromeLogo;
